import { DOMProps } from '@react-types/shared';
import { filterDOMProps } from 'react-aria/filterDOMProps';
import React, { ElementType, forwardRef } from 'react';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/text/text.module.css';
import {
  SapphireStyleProps,
  TextStyleProps,
  useTextStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

export interface TypographyBodyProps
  extends DOMProps,
    TextStyleProps,
    SapphireStyleProps,
    GlobalDomAttributes {
  children: React.ReactNode;
  /**
   * The actual DOM element to render.
   * @default 'p'
   */
  elementType?: string;
  /**
   * Sets the level of the hierarchy of the text.
   * @default 'md'
   */
  size?: 'lg' | 'md' | 'sm' | 'xs';
  /**
   * The semantic color of the text
   * @default 'primary'
   */
  color?:
    | 'primary'
    | 'secondary'
    | 'positive'
    | 'negative'
    | 'warning'
    | 'informative';
  /**
   * If text should be underlined.
   * @default false
   */
  isUnderlined?: boolean;

  /**
   * If text is bold.
   * @default false
   */
  isBold?: boolean;

  /**
   * If text is semibold.
   * @default false
   */
  isSemibold?: boolean;
}

/**
 * Body text. This is the most common text style. By default, it renders as a
 * paragraph (p), but you can render it as a span or something else.
 *
 * See:
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element#text_content
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element#inline_text_semantics
 */
export const Body = forwardRef(function Body(
  props: TypographyBodyProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  useThemeCheck();

  const {
    children,
    size = 'md',
    color = 'primary',
    elementType: CustomElement = 'p' as ElementType,
    isUnderlined = false,
    isBold = false,
    isSemibold = false,
    ...otherProps
  } = props;
  const { styleProps } = useTextStyleProps(otherProps);
  const sizeClassName = styles[`sapphire-text--body-${size}` as const];
  const colorClassName =
    color === 'primary'
      ? undefined
      : styles[`sapphire-text--${color}` as const];

  return (
    <CustomElement
      {...filterDOMProps(props, { global: true })}
      {...styleProps}
      className={clsx(
        styleProps.className,
        styles['sapphire-text'],
        sizeClassName,
        colorClassName,
        {
          [styles['sapphire-text--strong']]: isBold,
          [styles['sapphire-text--semibold']]: isSemibold,
          [styles['sapphire-text--underlined']]: isUnderlined,
        }
      )}
      style={styleProps.style}
      ref={ref}
    >
      {children}
    </CustomElement>
  );
});
